import { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, GraduationCap, AlertTriangle, CheckCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CONFIRM_PHRASE = 'DELETE TUTORIAL ACCOUNTS';

/**
 * Super-admin only Tutorial Accounts card.
 *
 * Backend endpoints (super-admin gated):
 *   GET  /admin/tutorial-providers/status   -> { total, expected, personas[] }
 *   POST /admin/tutorial-providers/seed     -> seed summary
 *   POST /admin/tutorial-providers/clean    -> requires { confirmation: 'DELETE TUTORIAL ACCOUNTS' }
 */
export function TutorialAccountsCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedSummary, setSeedSummary] = useState(null);

  const [showCleanDialog, setShowCleanDialog] = useState(false);
  const [cleanConfirmation, setCleanConfirmation] = useState('');
  const [cleaning, setCleaning] = useState(false);

  // Aaliyah demo data (bookings + earnings).
  const [demoStatus, setDemoStatus] = useState(null);
  const [demoSeeding, setDemoSeeding] = useState(false);
  const [showDemoCleanDialog, setShowDemoCleanDialog] = useState(false);
  const [demoCleanConfirmation, setDemoCleanConfirmation] = useState('');
  const [demoCleaning, setDemoCleaning] = useState(false);
  const DEMO_PHRASE = 'REMOVE DEMO DATA';

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, demoRes] = await Promise.all([
        api.get('/admin/tutorial-providers/status'),
        api.get('/admin/tutorial-providers/demo-data/status').catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setDemoStatus(demoRes.data);
      setAccessDenied(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Could not load tutorial status');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedSummary(null);
    try {
      const res = await api.post('/admin/tutorial-providers/seed');
      setSeedSummary(res.data);
      setStatus(res.data.status);
      const created = res.data.users_created;
      const skipped = res.data.users_skipped;
      const errors = res.data.errors?.length || 0;
      if (errors > 0) {
        toast.warning(`Seeded with ${errors} error${errors > 1 ? 's' : ''}, see summary below`);
      } else if (created === 0) {
        toast.success('All 9 tutorial accounts already exist (no changes)');
      } else {
        toast.success(`Created ${created} new account${created > 1 ? 's' : ''}, skipped ${skipped}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const handleClean = async () => {
    if (cleanConfirmation !== CONFIRM_PHRASE) {
      toast.error(`Type the confirmation phrase exactly: ${CONFIRM_PHRASE}`);
      return;
    }
    setCleaning(true);
    try {
      const res = await api.post('/admin/tutorial-providers/clean', { confirmation: cleanConfirmation });
      setStatus(res.data.status);
      const d = res.data.deleted;
      toast.success(`Removed ${d.users} accounts, ${d.services} services, ${d.reviews} reviews, ${d.videos} feed posts`);
      setShowCleanDialog(false);
      setCleanConfirmation('');
      setSeedSummary(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clean failed');
    } finally {
      setCleaning(false);
    }
  };

  const handleDemoSeed = async () => {
    setDemoSeeding(true);
    try {
      const res = await api.post('/admin/tutorial-providers/demo-data/seed');
      setDemoStatus(res.data.status || res.data);
      if (res.data.skipped) {
        toast.success(res.data.message || 'Demo data already seeded');
      } else {
        const c = res.data.status?.counts || {};
        toast.success(
          `Seeded ${res.data.upcoming || 0} upcoming, ${res.data.completed || 0} completed, ${res.data.cancelled || 0} cancelled bookings + £${(res.data.payout_amount || 0).toFixed(2)} payout`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demo seed failed');
    } finally {
      setDemoSeeding(false);
    }
  };

  const handleDemoClean = async () => {
    if (demoCleanConfirmation !== DEMO_PHRASE) {
      toast.error(`Type the confirmation phrase exactly: ${DEMO_PHRASE}`);
      return;
    }
    setDemoCleaning(true);
    try {
      const res = await api.post('/admin/tutorial-providers/demo-data/clean', { confirmation: demoCleanConfirmation });
      setDemoStatus(res.data.status || null);
      const d = res.data.deleted || {};
      toast.success(`Removed ${d.bookings || 0} bookings, ${d.transactions || 0} transactions, ${d.payouts || 0} payouts`);
      setShowDemoCleanDialog(false);
      setDemoCleanConfirmation('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demo clean failed');
    } finally {
      setDemoCleaning(false);
    }
  };

  if (accessDenied) {
    // Render nothing for non-super-admins so the card never appears.
    return null;
  }

  const total = status?.total || 0;
  const expected = status?.expected || 9;
  const fullySeeded = total === expected;

  return (
    <div className="card-surface p-6" data-testid="tutorial-accounts-card">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Tutorial Accounts</h3>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">
              9 demo provider profiles for product walk-throughs. Bookings disabled,
              excluded from KPIs, payouts and analytics. Super-admin only.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadStatus}
          className="text-stone-400 hover:text-stone-700 p-1"
          aria-label="Refresh"
          data-testid="tutorial-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            fullySeeded ? 'bg-green-100 text-green-800' : total === 0 ? 'bg-stone-100 text-stone-700' : 'bg-amber-100 text-amber-800'
          }`}
          data-testid="tutorial-active-count"
        >
          {loading ? '...' : `${total} / ${expected} active`}
        </span>
        {fullySeeded && (
          <span className="text-xs text-green-700 inline-flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Ready for filming
          </span>
        )}
      </div>

      {status?.personas && total > 0 && total < expected && (
        <div className="mb-4 text-xs text-stone-500">
          Missing: {status.personas.filter(p => !p.seeded).map(p => p.brand_name).join(', ')}
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <Button
          onClick={() => setShowSeedDialog(true)}
          className="btn-primary"
          disabled={seeding}
          data-testid="tutorial-seed-btn"
        >
          {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Seed 9 tutorial providers
        </Button>
        <Button
          onClick={() => setShowCleanDialog(true)}
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50"
          disabled={total === 0}
          data-testid="tutorial-clean-btn"
        >
          Remove all tutorial providers
        </Button>
      </div>

      {total > 0 && (
        <div className="mt-5 pt-5 border-t border-stone-100">
          <p className="text-xs text-stone-500 mb-2">
            Tutorial accounts are hidden from the public Discover and Feed by default.
            Use these links to see them in context for filming:
          </p>
          <div className="flex gap-2 flex-wrap">
            <a
              href="/discover?include_tutorials=true"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
              data-testid="tutorial-link-discover"
            >
              <ExternalLink className="h-3 w-3" /> Open Discover
            </a>
            <a
              href="/feed?include_tutorials=true"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
              data-testid="tutorial-link-feed"
            >
              <ExternalLink className="h-3 w-3" /> Open Feed
            </a>
            {status?.personas?.filter(p => p.seeded).slice(0, 3).map(p => (
              <a
                key={p.key}
                href={`/@${p.key === 'aaliyah-braids' ? 'aaliyahbraids' :
                       p.key === 'marcus-fades' ? 'marcusbarber' :
                       p.key === 'naomi-nails' ? 'naominails' :
                       p.key === 'tianna-lashes' ? 'tiannalashes' :
                       p.key === 'imani-glam' ? 'imaniglam' :
                       p.key === 'sade-naturals' ? 'sadenaturals' :
                       p.key === 'amara-mobile' ? 'amaramobile' :
                       p.key === 'jordan-studio' ? 'jordanstudio' :
                       'bisibrows'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-stone-50 text-stone-700 border border-stone-200 hover:bg-stone-100 transition-colors"
              >
                <ExternalLink className="h-3 w-3" /> {p.brand_name}
              </a>
            ))}
          </div>
          <p className="text-[11px] text-stone-400 mt-2">
            Login: <code className="font-mono">tutorial.&lt;key&gt;@zuriya.co.uk</code>, password <code className="font-mono">TutorialDemo2026!</code> (e.g. tutorial.aaliyah@zuriya.co.uk)
          </p>
        </div>
      )}

      {total > 0 && (
        <div className="mt-5 pt-5 border-t border-stone-100" data-testid="tutorial-demo-data-section">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div>
              <h4 className="text-sm font-semibold text-[#1A1A1A]">Aaliyah, Bookings & Earnings demo data</h4>
              <p className="text-xs text-stone-500 mt-0.5 max-w-xl">
                Populates Aaliyah's Bookings tab (4 upcoming, 6 completed, 1 cancelled+refund) and Earnings tab (~£1,180 gross,
                £177 commission, one £365 payout) so the dashboard tutorial isn't filmed against an empty state.
              </p>
            </div>
            {demoStatus?.seeded && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                <CheckCircle className="h-3 w-3 mr-1" /> {demoStatus.counts.bookings} bookings active
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            <Button
              onClick={handleDemoSeed}
              disabled={demoSeeding}
              className="btn-primary text-xs h-9"
              data-testid="tutorial-demo-seed-btn"
            >
              {demoSeeding ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : null}
              {demoStatus?.seeded ? 'Reseed (idempotent)' : 'Seed bookings & earnings'}
            </Button>
            <Button
              onClick={() => setShowDemoCleanDialog(true)}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50 text-xs h-9"
              disabled={!demoStatus?.seeded}
              data-testid="tutorial-demo-clean-btn"
            >
              Remove demo data
            </Button>
          </div>
          <p className="text-[11px] text-stone-400 mt-2">
            All demo bookings, transactions, and payouts are tagged so they never affect real platform totals.
          </p>
        </div>
      )}

      {seedSummary && (
        <div className="mt-4 p-3 bg-stone-50 border border-stone-200 rounded-md text-xs text-stone-700" data-testid="tutorial-seed-summary">
          <div className="font-medium text-stone-800 mb-1">Last seed run</div>
          <div>Created: {seedSummary.users_created} users, {seedSummary.providers_created} providers</div>
          <div>Skipped (already existed): {seedSummary.users_skipped} users, {seedSummary.providers_skipped} providers</div>
          <div>Founding: {seedSummary.founding}, Early: {seedSummary.early}</div>
          {seedSummary.errors?.length > 0 && (
            <div className="text-red-700 mt-1">
              Errors ({seedSummary.errors.length}):
              {seedSummary.errors.map((e) => (
                <div key={`${e.key}-${e.error}`} className="ml-2">- {e.key}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Seed confirmation modal */}
      <Dialog open={showSeedDialog} onOpenChange={setShowSeedDialog}>
        <DialogContent data-testid="tutorial-seed-modal">
          <DialogHeader>
            <DialogTitle>Seed tutorial providers</DialogTitle>
            <DialogDescription>
              This will create up to 9 demo provider accounts (4 Founding in Brixton,
              5 Early elsewhere in the UK). Bookings will be disabled. The action is
              idempotent, existing accounts will not be duplicated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowSeedDialog(false)}>Cancel</Button>
            <Button
              onClick={async () => { setShowSeedDialog(false); await handleSeed(); }}
              className="btn-primary"
              data-testid="tutorial-seed-confirm-btn"
            >
              Seed providers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clean confirmation modal, requires typed phrase */}
      <Dialog open={showCleanDialog} onOpenChange={(o) => { setShowCleanDialog(o); if (!o) setCleanConfirmation(''); }}>
        <DialogContent data-testid="tutorial-clean-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Remove all tutorial providers
            </DialogTitle>
            <DialogDescription>
              This deletes all 9 accounts plus their services, availability, reviews,
              feed posts, and portfolio images. Real customer data is not affected.
              <br /><br />
              Type <strong className="font-mono text-red-700">{CONFIRM_PHRASE}</strong> below to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tutorial-clean-confirm">Confirmation phrase</Label>
            <Input
              id="tutorial-clean-confirm"
              value={cleanConfirmation}
              onChange={(e) => setCleanConfirmation(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
              data-testid="tutorial-clean-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowCleanDialog(false)}>Cancel</Button>
            <Button
              onClick={handleClean}
              disabled={cleanConfirmation !== CONFIRM_PHRASE || cleaning}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="tutorial-clean-confirm-btn"
            >
              {cleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Demo data clean confirmation modal */}
      <Dialog open={showDemoCleanDialog} onOpenChange={(o) => { setShowDemoCleanDialog(o); if (!o) setDemoCleanConfirmation(''); }}>
        <DialogContent data-testid="tutorial-demo-clean-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Remove demo bookings & earnings
            </DialogTitle>
            <DialogDescription>
              This deletes all seeded bookings, transactions, reviews, and payouts attributed to Aaliyah for the
              tutorial. Real customer data is not affected.
              <br /><br />
              Type <strong className="font-mono text-red-700">{DEMO_PHRASE}</strong> below to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tutorial-demo-clean-confirm">Confirmation phrase</Label>
            <Input
              id="tutorial-demo-clean-confirm"
              value={demoCleanConfirmation}
              onChange={(e) => setDemoCleanConfirmation(e.target.value)}
              placeholder={DEMO_PHRASE}
              autoComplete="off"
              data-testid="tutorial-demo-clean-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowDemoCleanDialog(false)}>Cancel</Button>
            <Button
              onClick={handleDemoClean}
              disabled={demoCleanConfirmation !== DEMO_PHRASE || demoCleaning}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="tutorial-demo-clean-confirm-btn"
            >
              {demoCleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove demo data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
